import axios from "axios";
import { firebase } from "../../../firebaseConfig";
import router from "../../../router";
import {
  addProjectMutation,
  projectQuery,
  standardsQuery,
  addBuildingMutation,
  updateProjectMutation,
  updateBuildingMutation,
  addFloorMutation,
  updateFloorMutation,
  addRoomMutation,
  setRoomCornersMutation,
  updateRoomMutation,
  clearMeasurementPointsMutation,
  addMeasurementPointsMutation,
  setIlluminationTestsMutation,
  setFunctionalTestsMutation,
  setConditionsMutation,
  addIlluminationTestResultMutation,
  updateIlluminationTestResultMutation,
  deleteIlluminationTestResultMutation,
  addEquipmentMutation,
  setFunctionalTestZonesMutation,
  setFunctionalTestResultsMutation,
  deleteFloorMutation,
  deleteRoomMutation,
  deleteBuildingMutation,
  setRoomImageMutation,
  setProjectStateMutation,
  onSetState,
  onRevokeAccess,
  copySnapshotMutation,
  onSetFlag,
  flagQuery,
  generatePdfQuery,
  missingEquipmentUsersQuery,
  fileQuery,
  setFurnitureQuestionMutation,
  setMeasuredFlickerPercentageMutation,
  setMeasuredFlickerIndexMutation,
  setMeasuredRaMutation,
  setMeasuredRuglMutation,
} from "@/graphql/query";
import { client } from "@/main";
import dataURLtoFile from "@/utils/dataURLtoFile";
import { v4 as uuid } from "uuid";
import i18n from "../../../i18n";
import { textFieldTrim } from "@/utils/helpers";
import { eventBus } from "@/components/EventBus";
import { format } from "date-fns";

export const getFirebaseTimestamp = () =>
  firebase.firestore.Timestamp.now().toMillis();

export default {
  async getProjectById({ commit, state, rootState }, id) {
    try {
      const { data } = await client.query({
        query: projectQuery,
        fetchPolicy: "network-only",
        variables: {
          project_id: id,
        },
      });
      if (data.project.length === 0) {
        router.push("/project-unavailable");
        return;
      }
      commit("setProjectData", data.project[0]);
      if (
        !state.generatingPdf &&
        data.project[0].buildings?.[0]?.floors?.[0] &&
        !rootState.floorStore.floor_id
      ) {
        commit("buildingStore/setBuildingData", data.project[0].buildings[0], {
          root: true,
        });
        commit(
          "floorStore/setFloorData",
          data.project[0].buildings[0].floors[0],
          {
            root: true,
          }
        );
      }
    } catch (error) {
      console.error(`Error getting a project: ${error}`);
    }
  },
  async getAvailableStandards({ commit }) {
    try {
      const { data } = await client.query({
        query: standardsQuery,
        fetchPolicy: "network-only",
      });
      commit("setAvailableStandards", data.standards);
    } catch (error) {
      console.error(`Error getting a list of available standards: ${error}`);
    }
  },
  async copySnapshot({ commit }, snapshot_id) {
    commit("loaderStore/setIsLoading", true, { root: true });
    try {
      const {
        data: { copySnapshot },
      } = await client.mutate({
        mutation: copySnapshotMutation,
        variables: {
          snapshot_id,
        },
      });
      const { buildings, project_id } = copySnapshot;
      commit("setProjectData", copySnapshot);
      if (buildings[0]?.floors?.[0]) {
        commit("buildingStore/setBuildingData", buildings[0], {
          root: true,
        });
        commit("floorStore/setFloorData", buildings[0].floors[0], {
          root: true,
        });
      }
      commit("loaderStore/setIsLoading", false, { root: true });
      await router.push(`/project/${project_id}`);
      eventBus.$emit("newDuplicateAdded");
    } catch (error) {
      console.error(`Error copying the snapshot: ${error}`);
    }
  },
  async getImageOptimizationFlag({ commit }) {
    try {
      const { data } = await client.query({
        query: flagQuery,
        fetchPolicy: "network-only",
        variables: { name: "optimize_images" },
      });
      const active = data.flag.length > 0 ? data.flag[0].active : false;
      commit("setImageOptimizationEnabled", active);
    } catch (error) {
      console.error(`Error getting image optimization flag: ${error}`);
    }
  },
  async getMaintenanceFlag({ commit }) {
    try {
      const { data } = await client.query({
        query: flagQuery,
        fetchPolicy: "network-only",
        variables: {
          name: "maintenance",
        },
      });
      let active = false;
      if (data.flag.length > 0) {
        active = data.flag[0].active;
      }
      commit("setMaintenance", active);
    } catch (error) {
      console.error(`Error getting a flag: ${error}`);
    }
  },
  async addNewProject({ state, commit }) {
    const { name, standard, available_standards } = state;
    const buildingName = i18n.t("building.default_building");
    const floorName = i18n.t("floor.default_floor");
    const standard_name = available_standards.find(
      (item) => item.standard_id === standard
    ).standard_name;
    try {
      const {
        data: { addProject },
      } = await client.mutate({
        mutation: addProjectMutation,
        variables: {
          name,
          standard_id: standard,
          standard_name,
          building_name: buildingName,
          floor_name: floorName,
        },
      });
      const { buildings, project_id } = addProject;
      commit("setProjectData", addProject);
      commit("buildingStore/setBuildingData", buildings[0], {
        root: true,
      });
      commit("floorStore/setFloorData", buildings[0].floors[0], {
        root: true,
      });
      router.push(`project/${project_id}`);
    } catch (error) {
      console.error(`Error adding a new project: ${error}`);
    }
  },
  async setProjectState({ commit }, { project_id, newState }) {
    commit("loaderStore/setIsLoading", true, { root: true });
    try {
      const { data } = await client.mutate({
        mutation: setProjectStateMutation,
        variables: {
          project_id,
          state: newState,
        },
      });
      if (newState === "deleted") {
        commit("projectsStore/deleteProject", project_id, { root: true });
      } else {
        commit("updateProjectState", data.setState.state);
      }
      commit("loaderStore/setIsLoading", false, { root: true });
    } catch (error) {
      console.error(`Error updating project state: ${error}`);
      commit("loaderStore/setIsLoading", false, { root: true });
    }
  },
  async subscribeSetState({ commit }, project_id) {
    const stateSubscription = client
      .subscribe({
        query: onSetState,
        variables: {
          project_id,
        },
      })
      .subscribe({
        next: (data) => {
          const { state } = data.data.onSetState;
          commit("setNewState", state);
        },
        error: (error) =>
          console.error(`Error subscribing to project state: ${error}`),
      });
    commit("setStateSubscription", stateSubscription);
  },
  async unsubscribeSetState({ state }) {
    const { stateSubscription } = state;
    stateSubscription.unsubscribe();
  },
  async subscribeRevokeAccess({ commit, rootState }, project_id) {
    const { email } = rootState.authStore;
    const revokeAccessSubscription = client
      .subscribe({
        query: onRevokeAccess,
        variables: {
          project_id,
          email,
        },
      })
      .subscribe({
        next: () => {
          commit("setNewState", "revoked");
        },
        error: (error) =>
          console.error(`Error subscribing to revoke access: ${error}`),
      });
    commit("setRevokeAccessSubscription", revokeAccessSubscription);
  },
  async unsubscribeRevokeAccess({ state }) {
    const { revokeAccessSubscription } = state;
    revokeAccessSubscription.unsubscribe();
  },
  async updateProjectNameAndStandard({ state, commit }, standard_id) {
    const projectName = state.editedName || state.name;
    const standard_name = state.available_standards.find(
      (item) => item.standard_id === standard_id
    ).standard_name;
    try {
      const { data } = await client.mutate({
        mutation: updateProjectMutation,
        variables: {
          project_id: state.project_id,
          name: projectName,
          standard_id,
          standard_name,
        },
      });
      const project = data.updateProject;
      commit("updateProjectName", project.name);
      commit("updateStandard", project.standard_id);
    } catch (error) {
      console.error(`Error updating project: ${error}`);
    }
  },
  async addBuilding({ commit, rootState, state }) {
    const floorName = i18n.t("floor.default_floor");
    try {
      const { data } = await client.mutate({
        mutation: addBuildingMutation,
        variables: {
          name: rootState.buildingStore.name,
          project_id: state.project_id,
          floor_name: floorName,
          comments: rootState.buildingStore.comments,
          address: rootState.buildingStore.address,
        },
      });
      commit("addBuilding", data.addBuilding);
      commit("buildingStore/setBuildingData", data.addBuilding, { root: true });
      commit("floorStore/setFloorData", data.addBuilding.floors[0], {
        root: true,
      });
    } catch (error) {
      console.error(`Error adding a building: ${error}`);
    }
  },
  async updateBuilding({ rootState, commit }, building_id) {
    try {
      const { data } = await client.mutate({
        mutation: updateBuildingMutation,
        variables: {
          building_id: building_id,
          name: rootState.buildingStore.name,
          address: rootState.buildingStore.address,
          comments: rootState.buildingStore.comments,
        },
      });
      commit("updateBuilding", data.updateBuilding);
      commit("buildingStore/setBuildingData", data.updateBuilding, {
        root: true,
      });
    } catch (error) {
      console.error(`Error updating building: ${error}`);
    }
  },
  async deleteBuilding({ commit }, building_id) {
    try {
      await client.mutate({
        mutation: deleteBuildingMutation,
        variables: { building_id },
      });
      commit("deleteBuilding", building_id);
    } catch (error) {
      console.error(`Error deleting a building: ${error}`);
    }
  },
  async updateEquipment({ state, commit }, equipments) {
    try {
      const { data } = await client.mutate({
        mutation: addEquipmentMutation,
        variables: {
          equipments: equipments,
          project_id: state.project_id,
        },
      });
      commit("setEquipments", data.addEquipment.equipments);
    } catch (error) {
      console.log(`Error adding a equipment: ${error}`);
    }
  },
  async addFloor({ commit, rootState }) {
    const building_id = rootState.buildingStore.building_id;
    try {
      const { data } = await client.mutate({
        mutation: addFloorMutation,
        variables: {
          building_id,
          name: rootState.floorStore.name,
        },
      });
      commit("addFloor", { floor: data.addFloor, building_id });
      commit("floorStore/setFloorData", data.addFloor, { root: true });
    } catch (error) {
      console.error(`Error adding a floor: ${error}`);
    }
  },
  async updateFloor(
    { commit, rootState },
    { building_id, name, size, uploadingPlan }
  ) {
    commit("loaderStore/setIsLoading", true, { root: true });
    try {
      const { data } = await client.mutate({
        mutation: updateFloorMutation,
        variables: {
          floor_id: rootState.floorStore.floor_id,
          name: rootState.floorStore.name,
          plan_url: rootState.floorStore.planURL,
        },
      });
      commit("updateFloor", {
        floor: { ...data.updateFloor, fileName: name, fileSize: size },
        building_id,
      });
      commit("floorStore/setFloorData", data.updateFloor, { root: true });
      if (uploadingPlan) {
        commit("loaderStore/setIsLoading", false, { root: true });
      }
    } catch (error) {
      console.error(`Error updating a floor: ${error}`);
    }
    commit("loaderStore/setIsLoading", false, { root: true });
  },
  async deleteFloor({ rootState, commit }, floor_id) {
    const building_id = rootState.buildingStore.building_id;
    try {
      await client.mutate({
        mutation: deleteFloorMutation,
        variables: {
          floor_id,
        },
      });
      commit("deleteFloor", {
        building_id,
        floor_id,
      });
    } catch (error) {
      console.error(`Error deleting a floor: ${error}`);
    }
  },
  async updateRoom({ commit, dispatch }, room) {
    commit("loaderStore/setIsLoading", true, {
      root: true,
    });

    try {
      const { data } = await client.mutate({
        mutation: updateRoomMutation,
        variables: {
          ...room,
        },
      });
      const updatedRoom = data.updateRoom;
      dispatch("setRoomCorners", {
        room_id: updatedRoom.room_id,
        updating: true,
      });
    } catch (error) {
      console.error(`Error updating a room: ${error}`);
      commit("roomStore/resetRoomState", null, {
        root: true,
      });
    }
    commit("loaderStore/setIsLoading", false, {
      root: true,
    });
  },
  async resetRoomTypes({ commit }, roomObjects) {
    commit("loaderStore/setIsLoading", true, {
      root: true,
    });
    try {
      for (const roomObj of roomObjects) {
        const { data } = await client.mutate({
          mutation: updateRoomMutation,
          variables: {
            ...roomObj.room,
          },
        });
        commit("updateRoomData", {
          updatedRoomData: data.updateRoom,
          building_id: roomObj.building_id,
          floor_id: roomObj.floor_id,
        });
      }
      commit("loaderStore/setIsLoading", false, {
        root: true,
      });
    } catch (error) {
      console.error(`Error resetting room types: ${error}`);
      commit("loaderStore/setIsLoading", false, {
        root: true,
      });
    }
  },
  async addRoom({ rootState, commit, dispatch }, { room, dataURL }) {
    commit("loaderStore/setIsLoading", true, {
      root: true,
    });

    const building_id = rootState.buildingStore.building_id;
    const floor_id = rootState.floorStore.floor_id;
    const roomDrawingReady = rootState.roomStore.closed;
    const testsSpecified =
      rootState.roomStore.illumination_tests.length > 0 ||
      rootState.roomStore.functional_tests.length > 0;
    try {
      const { data } = await client.mutate({
        mutation: addRoomMutation,
        variables: {
          floor_id,
          ...room,
        },
      });
      if (roomDrawingReady || testsSpecified) {
        dispatch("setRoomCorners", {
          room_id: data.addRoom.room_id,
          updating: false,
        });
      } else {
        const addedRoom = data.addRoom;
        commit("addRoom", { addedRoom, building_id, floor_id });
        commit("roomStore/resetRoomState", null, {
          root: true,
        });
      }
      const { room_id } = data.addRoom;
      if (dataURL) {
        const file = dataURLtoFile(dataURL, room_id);
        dispatch("roomStore/uploadRoomFile", file, {
          root: true,
        }).then(({ url, optimizedUrl }) => {
          dispatch("setRoomImage", { room_id, url, optimizedUrl });
        });
      }
    } catch (error) {
      console.error(`Error adding a room: ${error}`);
      commit("roomStore/resetRoomState", null, {
        root: true,
      });
    }
    commit("loaderStore/setIsLoading", false, { root: true });
  },
  async deleteRoom({ rootState, commit }, room_id) {
    const building_id = rootState.buildingStore.building_id;
    const floor_id = rootState.floorStore.floor_id;
    try {
      await client.mutate({
        mutation: deleteRoomMutation,
        variables: {
          room_id,
        },
      });
      commit("deleteRoom", {
        building_id,
        floor_id,
        room_id,
      });
    } catch (error) {
      console.error(`Error deleting a room: ${error}`);
    }
  },
  async setRoomImage({ commit }, { room_id, url, optimizedUrl }) {
    try {
      await client.mutate({
        mutation: setRoomImageMutation,
        variables: {
          room_id,
          room_image_url: url,
          room_image_optimized_url: optimizedUrl,
        },
      });
      commit("roomStore/setRoomImageUrl", url, { root: true });
      commit("roomStore/setRoomImageOptimizedUrl", optimizedUrl, {
        root: true,
      });
    } catch (error) {
      console.error(`Error setting room image: ${error}`);
    }
  },
  async setRoomCorners({ rootState, commit, dispatch }, { room_id, updating }) {
    const cornersArray = rootState.roomStore.corners;
    const corners = [];
    for (let i = 0; i < cornersArray.length - 1; i = i + 2) {
      corners.push({
        x: cornersArray[i],
        y: cornersArray[i + 1],
      });
    }
    try {
      await client.mutate({
        mutation: setRoomCornersMutation,
        variables: {
          room_id,
          corners,
        },
      });
      dispatch("setMeasurementPoints", { room_id, updating });
    } catch (error) {
      console.error(`Error setting room corners: ${error}`);
      commit("roomStore/resetRoomState", null, {
        root: true,
      });
    }
  },
  async setMeasurementPoints(
    { rootState, commit, dispatch },
    { room_id, updating }
  ) {
    const measurement_points = rootState.roomStore.measurement_points.map(
      (point) => {
        return {
          is_active: point.is_active,
          location: point.location,
          x: point.x,
          y: point.y,
        };
      }
    );
    try {
      if (updating) {
        await client.mutate({
          mutation: clearMeasurementPointsMutation,
          variables: {
            room_id,
          },
        });
      }
      for (let i = 0; i < measurement_points.length; i += 300) {
        await client.mutate({
          mutation: addMeasurementPointsMutation,
          variables: {
            room_id,
            measurement_points: measurement_points.slice(i, i + 300),
          },
        });
      }
      dispatch("setIlluminationTests", { room_id, updating });
    } catch (error) {
      console.error(`Error setting room measurement points: ${error}`);
      commit("roomStore/resetRoomState", null, {
        root: true,
      });
    }
  },
  async setIlluminationTests(
    { rootState, commit, dispatch },
    { room_id, updating, shouldSetFunctionalTests = true }
  ) {
    const tests = rootState.roomStore.illumination_tests.map((test) => {
      const test_requirements = textFieldTrim(test.test_requirements);
      return {
        name: test.name,
        test_requirements,
        average_illuminance: test.average_illuminance,
        illuminance_deviation: test.illuminance_deviation,
        uniformity: test.uniformity,
        required_ra: test.required_ra,
        required_rugl: test.required_rugl,
        measured_ra: test.measured_ra,
        measured_rugl: test.measured_rugl,
        required_flicker_percentage: test.required_flicker_percentage,
        required_flicker_index: test.required_flicker_index,
        measured_flicker_percentage: test.measured_flicker_percentage,
        measured_flicker_index: test.measured_flicker_index,
        include_flicker: test.include_flicker,
        include_ra_rugl_maintenance: test.include_ra_rugl_maintenance,
        maintenance_factor: test.maintenance_factor,
        furniture: test.furniture,
      };
    });
    try {
      await client.mutate({
        mutation: setIlluminationTestsMutation,
        variables: {
          room_id,
          tests,
        },
      });
      if (shouldSetFunctionalTests) {
        dispatch("setFunctionalTests", { room_id, updating });
      }
    } catch (error) {
      console.error(`Error setting illumination tests for the room: ${error}`);
      commit("roomStore/resetRoomState", null, {
        root: true,
      });
    }
  },
  async setFunctionalTests({ rootState, commit }, { room_id, updating }) {
    commit("loaderStore/setIsLoading", true, { root: true });

    const building_id = rootState.buildingStore.building_id;
    const floor_id = rootState.floorStore.floor_id;

    const tests = !rootState.roomStore.functional_tests
      ? []
      : rootState.roomStore.functional_tests?.map((test) => {
          const test_requirements = textFieldTrim(test.test_requirements);
          const testInput = {
            name: test.name,
            test_requirements: test_requirements,
            daylight_control_enabled: test.daylight_control_enabled,
            daylight_zones_enabled: test.daylight_control_enabled
              ? test.daylight_zones_enabled
              : false,
            motion_detectors_enabled: test.motion_detectors_enabled,
          };
          if (!test.daylight_control_enabled) {
            testInput.daylight_control_disabled_comment =
              textFieldTrim(test.daylight_control_disabled_comment) || "";
          }
          if (test.daylight_control_enabled && !test.daylight_zones_enabled) {
            testInput.daylight_zones_disabled_comment =
              textFieldTrim(test.daylight_zones_disabled_comment) || "";
          }
          if (test.daylight_control_enabled && test.daylight_zones_enabled) {
            testInput.required_number_of_zones_unknown =
              test.required_number_of_zones_unknown;
            if (!test.required_number_of_zones_unknown) {
              testInput.required_number_of_zones =
                test.required_number_of_zones;
            }
          }
          if (!test.motion_detectors_enabled) {
            testInput.motion_detectors_disabled_comment =
              textFieldTrim(test.motion_detectors_disabled_comment) || "";
          } else {
            testInput.light_on_automatic = test.light_on_automatic;
            testInput.light_off_delay = test.light_off_delay;
          }
          return testInput;
        });

    try {
      const { data } = await client.mutate({
        mutation: setFunctionalTestsMutation,
        variables: {
          room_id,
          tests,
        },
      });
      const room = data.setFunctionalTests;
      if (updating) {
        commit("updateRoomData", {
          updatedRoomData: room,
          building_id,
          floor_id,
        });
      } else {
        commit("addRoom", { addedRoom: room, building_id, floor_id });
      }
      commit("roomStore/resetRoomState", null, {
        root: true,
      });
      commit("roomStore/setUpdatingRoom", false, {
        root: true,
      });
    } catch (error) {
      console.error(`Error setting functional tests for the room: ${error}`);
    }
    if (updating) {
      commit("loaderStore/setIsLoading", false, {
        root: true,
      });
    }
    commit("loaderStore/setIsLoading", false, {
      root: true,
    });
  },
  async setMeasurementConditions(
    { commit, rootState },
    { illumination_test_id, measurement_conditions }
  ) {
    const building_id = rootState.buildingStore.building_id;
    const floor_id = rootState.floorStore.floor_id;
    const room_id = rootState.roomStore.room_id;
    try {
      await client.mutate({
        mutation: setConditionsMutation,
        variables: {
          illumination_test_id,
          measurement_conditions,
        },
        optimisticResponse: () => ({
          setConditions: {
            measurement_conditions,
            __typename: "IlluminationTest",
          },
        }),
        update: (cache, { data: { setConditions } }) => {
          const conditions = setConditions.measurement_conditions;
          commit(
            "roomStore/setMeasurementConditionsForIlluminationTest",
            {
              illumination_test_id,
              measurement_conditions: conditions,
            },
            {
              root: true,
            }
          );
          commit("setMeasurementConditions", {
            building_id,
            floor_id,
            room_id,
            illumination_test_id,
            measurement_conditions: conditions,
          });
        },
      });
    } catch (error) {
      console.error(
        `Error setting measurement conditions for the test: ${error}`
      );
    }
  },
  async setMeasuredRugl(_, { illumination_test_id, measured_rugl }) {
    try {
      await client.mutate({
        mutation: setMeasuredRuglMutation,
        variables: {
          illumination_test_id,
          measured_rugl,
        },
      });
    } catch (error) {
      console.error(`Error setting measured Rugl for the test: ${error}`);
    }
  },
  async setMeasuredRa(_, { illumination_test_id, measured_ra }) {
    try {
      await client.mutate({
        mutation: setMeasuredRaMutation,
        variables: {
          illumination_test_id,
          measured_ra,
        },
      });
    } catch (error) {
      console.error(`Error setting measured Ra for the test: ${error}`);
    }
  },
  async setMeasuredFlickerIndex(
    _,
    { illumination_test_id, measured_flicker_index }
  ) {
    try {
      await client.mutate({
        mutation: setMeasuredFlickerIndexMutation,
        variables: {
          illumination_test_id,
          measured_flicker_index,
        },
      });
    } catch (error) {
      console.error(
        `Error setting measured flicker index for the test: ${error}`
      );
    }
  },
  async setMeasuredFlickerPercentage(
    _,
    { illumination_test_id, measured_flicker_percentage }
  ) {
    try {
      await client.mutate({
        mutation: setMeasuredFlickerPercentageMutation,
        variables: {
          illumination_test_id,
          measured_flicker_percentage,
        },
      });
    } catch (error) {
      console.error(
        `Error setting measured flicker percentage for the test: ${error}`
      );
    }
  },
  async setFurnitureQuestion(_, { illumination_test_id, furniture }) {
    try {
      await client.mutate({
        mutation: setFurnitureQuestionMutation,
        variables: {
          illumination_test_id,
          furniture,
        },
      });
    } catch (error) {
      console.error(`Error setting furniture for the test: ${error}`);
    }
  },
  async addIlluminationTestResult(
    { commit, rootState },
    { illumination_test_id, illumination_test_result }
  ) {
    const building_id = rootState.buildingStore.building_id;
    const floor_id = rootState.floorStore.floor_id;
    const room_id = rootState.roomStore.room_id;
    const illumination_test_results =
      rootState.roomStore.illumination_tests.find(
        (test) => test.illumination_test_id === illumination_test_id
      ).illumination_test_results;
    try {
      await client.mutate({
        mutation: addIlluminationTestResultMutation,
        variables: {
          illumination_test_id,
          illumination_test_result,
        },
        optimisticResponse: () => ({
          addIlluminationTestResult: {
            ...rootState.roomStore.illumination_tests.find(
              (test) => test.illumination_test_id === illumination_test_id
            ),
            illumination_test_results: [
              ...illumination_test_results,
              {
                equipment_id: uuid(),
                illumination_test_result_id: uuid(),
                measured_lux: illumination_test_result.measured_lux,
                measurement_point_id:
                  illumination_test_result.measurement_point_id,
                __typename: "IlluminationTestResult",
              },
            ],
          },
        }),
        update: (cache, { data: { addIlluminationTestResult } }) => {
          const allResults =
            addIlluminationTestResult.illumination_test_results;
          commit(
            "roomStore/setResultForIlluminationTest",
            {
              illumination_test_id,
              allResults,
              measurement_point_id:
                illumination_test_result.measurement_point_id,
            },
            {
              root: true,
            }
          );
          commit("setIlluminationResults", {
            building_id,
            floor_id,
            room_id,
            illumination_test_id,
            illumination_test_results: allResults,
            measurement_point_id: illumination_test_result.measurement_point_id,
          });
        },
      });
    } catch (error) {
      `Error adding an illumination test's measurement result: ${error}`;
    }
  },
  async updateIlluminationTestResult(
    { commit, rootState },
    {
      illumination_test_id,
      illumination_test_result_id,
      illumination_test_result,
    }
  ) {
    const building_id = rootState.buildingStore.building_id;
    const floor_id = rootState.floorStore.floor_id;
    const room_id = rootState.roomStore.room_id;
    try {
      await client.mutate({
        mutation: updateIlluminationTestResultMutation,
        variables: {
          illumination_test_result_id,
          illumination_test_id,
          illumination_test_result,
        },
        optimisticResponse: () => ({
          updateIlluminationTestResult: {
            equipment_id: uuid(),
            illumination_test_result_id,
            measured_lux: illumination_test_result.measured_lux,
            measurement_point_id: illumination_test_result.measurement_point_id,
            __typename: "IlluminationTestResult",
          },
        }),
        update: (cache, { data: { updateIlluminationTestResult } }) => {
          const updatedResult = updateIlluminationTestResult;
          commit(
            "roomStore/updateResultForIlluminationTest",
            {
              illumination_test_id,
              illumination_test_result_id,
              updatedResult,
            },
            {
              root: true,
            }
          );
          commit("updateIlluminationResults", {
            building_id,
            floor_id,
            room_id,
            illumination_test_id,
            illumination_test_result_id,
            updatedResult,
          });
        },
      });
    } catch (error) {
      `Error adding an illumination test's measurement result: ${error}`;
    }
  },
  async deleteIlluminationResult(
    { rootState, commit },
    { illumination_test_id, illumination_test_result_id }
  ) {
    const building_id = rootState.buildingStore.building_id;
    const floor_id = rootState.floorStore.floor_id;
    const room_id = rootState.roomStore.room_id;
    try {
      await client.mutate({
        mutation: deleteIlluminationTestResultMutation,
        variables: {
          illumination_test_result_id,
        },
      });
      commit("deleteIlluminationTestResult", {
        building_id,
        floor_id,
        room_id,
        illumination_test_id,
        illumination_test_result_id,
      });
    } catch (error) {
      `Error deleting an illumination test's measurement result: ${error}`;
    }
  },
  async setFunctionalTestZones(
    { commit, rootState },
    { functional_test_id, actual_number_of_zones }
  ) {
    const building_id = rootState.buildingStore.building_id;
    const floor_id = rootState.floorStore.floor_id;
    const room_id = rootState.roomStore.room_id;
    try {
      await client.mutate({
        mutation: setFunctionalTestZonesMutation,
        variables: {
          functional_test_id,
          actual_number_of_zones,
        },
        optimisticResponse: () => ({
          setFunctionalTestZones: {
            ...rootState.roomStore.functional_tests.find(
              (test) => test.functional_test_id === functional_test_id
            ),
            actual_number_of_zones,
          },
        }),
        update: (cache, { data: { setFunctionalTestZones } }) => {
          const savedNumberOfZones =
            setFunctionalTestZones.actual_number_of_zones;
          commit(
            "roomStore/setActualNumberOfZones",
            {
              functional_test_id,
              savedNumberOfZones,
            },
            { root: true }
          );
          commit("updateActualNumberOfZones", {
            building_id,
            floor_id,
            room_id,
            functional_test_id,
            savedNumberOfZones,
          });
        },
      });
    } catch (error) {
      `Error adding an actual amount of daylight zones in the room: ${error}`;
    }
  },
  async setFunctionalTestResults({ commit, rootState }, functional_test_id) {
    const building_id = rootState.buildingStore.building_id;
    const floor_id = rootState.floorStore.floor_id;
    const room_id = rootState.roomStore.room_id;
    const results = rootState.roomStore.functional_tests
      .find((test) => test.functional_test_id === functional_test_id)
      .functional_test_results.map((result) => {
        const comment = textFieldTrim(result.comment);
        return {
          answer: result.answer,
          comment: comment,
          question_identifier: result.question_identifier,
        };
      });
    try {
      const { data } = await client.mutate({
        mutation: setFunctionalTestResultsMutation,
        variables: {
          functional_test_id,
          results,
        },
      });
      const savedResults =
        data.setFunctionalTestResults.functional_test_results;
      commit("updateFunctionalTestResults", {
        building_id,
        floor_id,
        room_id,
        functional_test_id,
        savedResults,
      });
    } catch (error) {
      `Error setting functional test results: ${error}`;
    }
  },
  // eslint-disable-next-line no-unused-vars
  async retrieveFile({ commit }, fileurl) {
    if (!fileurl) return;
    try {
      const { data } = await client.query({
        query: fileQuery,
        fetchPolicy: "network-only",
        variables: {
          filename: fileurl,
          type: "floor",
          is_upload: false,
        },
      });
      return data.file.url;
    } catch (error) {
      console.error(`Error getting floorplan image: ${error}`);
    }
  },
  async uploadFloorFile({ commit, dispatch, rootState }, file) {
    const building_id = rootState.buildingStore.building_id;
    if (!file) return;
    try {
      commit("loaderStore/setIsLoading", true, { root: true });
      // not a great filename to be used
      // some hash would be better
      const filename = `${file.name} ${new Date().getTime()}`;
      const { data } = await client.query({
        query: fileQuery,
        fetchPolicy: "network-only",
        variables: {
          filename: filename,
          type: "floor",
          is_upload: true,
        },
      });
      const url = data.file.url;
      return axios
        .put(url, file, {
          headers: {
            "Content-Type": file.type,
          },
        })
        .then(() => {
          commit(
            "floorStore/updatePlanURL",
            { url: filename, name: file.name, size: file.size },
            { root: true }
          );

          dispatch("updateFloor", {
            building_id,
            name: file.name,
            size: file.size,
            uploadingPlan: true,
          });
        })
        .catch((err) => {
          console.log(err);
        })
        .finally(() => {
          commit("loaderStore/setIsLoading", false, { root: true });
        });
    } catch (error) {
      console.error(`Error uploading floorplan: ${error}`);
    }
  },
  async subscribeSetFlag({ commit }) {
    const flagSubscription = client
      .subscribe({
        query: onSetFlag,
        variables: {
          name: "maintenance",
        },
      })
      .subscribe({
        next: (data) => {
          const { active } = data.data.onSetFlag;
          commit("setMaintenance", active);
        },
        error: (error) =>
          console.error(`Error subscribing to maintenance flag: ${error}`),
      });
    commit("setMaintenanceSubscription", flagSubscription);
  },
  async unsubscribeSetFlag({ state }) {
    const { flagSubscription } = state;
    flagSubscription.unsubscribe();
  },
  async getMissingEquipmentUsers({ commit }, id) {
    try {
      const { data } = await client.query({
        query: missingEquipmentUsersQuery,
        fetchPolicy: "network-only",
        variables: {
          project_id: id,
        },
      });
      commit("setMissingEquipmentUsers", data.missing_equipment_users);
      return true;
    } catch (error) {
      console.error(
        `Error getting a list of missing equipment users: ${error}`
      );
      return false;
    }
  },
  async generatePdf({ state, rootState, commit }) {
    const localFullDate = format(new Date(), "dd.MM.yyyy HH:mm");
    const localDate = format(new Date(), "dd.MM.yyyy");
    const fileName = `${state.name}_${localDate}.pdf`;
    const standard_name = state.available_standards.find(
      (item) => item.standard_id === state.standard
    ).standard_name;
    const { email, fullName } = rootState.authStore;
    const projectManager = fullName.length === 0 ? email : fullName;
    const timestamp = Date.now();
    const fileKey = `${state.name}_${timestamp}.pdf`;
    try {
      const payload = JSON.stringify({
        project: {
          name: state.name,
          buildings: state.buildings,
          state: state.state,
          standard_name,
        },
        imageOptimizationEnabled:
          rootState.projectStore.imageOptimizationEnabled,
        timestamp,
        localFullDate,
        localDate,
        projectManager,
        version: process.env.VUE_APP_VERSION,
        bucket: process.env.VUE_APP_FILE_BUCKET_NAME,
        region: process.env.VUE_APP_AWS_REGION,
      });
      const { data } = await client.query({
        query: generatePdfQuery,
        fetchPolicy: "network-only",
        variables: {
          project: payload,
        },
      });
      if (rootState.loaderStore.cancelPdfGeneration) {
        commit("resetProjectState");
        commit("buildingStore/resetBuildingState", null, { root: true });
        commit("floorStore/resetFloorState", null, { root: true });
        return;
      }
      await fetch(data.pdf.data_url)
        .then((res) => res.text())
        .then((res) => {
          const link = document.createElement("a");
          link.href = res;
          link.download = fileName;
          link.click();
        });
    } catch (error) {
      console.error(`Error generating pdf: ${error}`);
      return new Promise((resolve) => {
        let pdfUrl = "";
        let secondsWaited = 30;
        let interval = setInterval(async () => {
          if (
            secondsWaited === 30 ||
            secondsWaited === 330 ||
            secondsWaited === 630
          ) {
            try {
              const { data } = await client.query({
                query: fileQuery,
                fetchPolicy: "network-only",
                variables: {
                  filename: fileKey,
                  type: "report",
                  is_upload: false,
                },
              });
              pdfUrl = data.file.url;
            } catch (error) {
              console.error(`Error getting URL for PDF report: ${error}`);
            }
          }

          secondsWaited = secondsWaited + 5;
          if (
            rootState.loaderStore.cancelPdfGeneration ||
            secondsWaited >= 900
          ) {
            commit("resetProjectState");
            commit("buildingStore/resetBuildingState", null, { root: true });
            commit("floorStore/resetFloorState", null, { root: true });
            clearInterval(interval);
            interval = null;
            resolve();
            return;
          }
          fetch(pdfUrl)
            .then((res) => {
              if (res.ok) {
                clearInterval(interval);
                interval = null;
              }
              res.text().then((res) => {
                if (res.substring(0, 20) === "data:application/pdf") {
                  const link = document.createElement("a");
                  link.href = res;
                  link.download = fileName;
                  link.click();
                  resolve();
                }
              });
            })
            .catch((error) => {
              console.log(error);
            });
        }, 5000);
      });
    }
  },
};
